import { LitElement, PropertyValues, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';

import { Dialog } from '@material/mwc-dialog';
import { GetPointResponse } from '../types';
import { isDefined } from '../helpers/utils';
import topicData from '../helpers/state/topic-data';

/**
 * Modal for displaying and customizing static map embed code
 */
@customElement('static-map-embed-modal')
export class StaticMapEmbedModal extends LitElement {
    @query('#modal')
    private readonly modal!: Dialog;

    @property({ type: Object })
    point?: GetPointResponse;

    @state()
    private embedWidth: number = 1000;

    @state()
    private embedHeight: number = 500;

    @state()
    private layout: string = 'map-right-info-left';

    open(state: boolean = true) {
        this.modal.open = state;
    }

    protected async firstUpdated(_changedProperties: PropertyValues): Promise<void> {
        await topicData.getCommandCenterShareLink();
    }

    private getEmbedUrl(for_embedding: boolean = false): string {
        if (!isDefined(this.point) || !isDefined(topicData.topic)) {
            return '';
        }

        const link = topicData.publicShareLink;
        const pointId = this.point._id.$oid;

        let embedUrl = `${link}?point_id=${pointId}&view=static-map-embed`;

        const widthParam = for_embedding ? Math.round(this.embedWidth * 0.90) : this.embedWidth;
        const heightParam = for_embedding ? Math.round(this.embedHeight * 0.90) : this.embedHeight;

        embedUrl += `&layout=${this.layout}&width=${widthParam}&height=${heightParam}`;

        return embedUrl;
    }

    private getIframeCode(): string {
        const embedUrl = this.getEmbedUrl(true);
        return `<iframe src="${embedUrl}" width="${this.embedWidth}" height="${this.embedHeight}" style="border:0" allowfullscreen></iframe>`;
    }

    private async copyUrl() {
        const embedUrl = this.getEmbedUrl();
        await navigator.clipboard.writeText(embedUrl);
        this.dispatchEvent(new CustomEvent('show-toast', {
            detail: { message: 'URL copied to clipboard!' },
            bubbles: true,
            composed: true
        }));
    }

    private async copyIframe() {
        const iframeCode = this.getIframeCode();
        await navigator.clipboard.writeText(iframeCode);
        this.dispatchEvent(new CustomEvent('show-toast', {
            detail: { message: 'Iframe code copied to clipboard!' },
            bubbles: true,
            composed: true
        }));
    }

    private onWidthInput(e: Event) {
        const input = e.target as HTMLInputElement;
        const value = parseInt(input.value, 10);
        if (!isNaN(value) && value > 0) {
            this.embedWidth = value;
        }
    }

    private onHeightInput(e: Event) {
        const input = e.target as HTMLInputElement;
        const value = parseInt(input.value, 10);
        if (!isNaN(value) && value > 0) {
            this.embedHeight = value;
        }
    }

    private onLayoutChange(e: Event) {
        const select = e.target as HTMLSelectElement;
        const newLayout = select.value;
        const oldLayout = this.layout;
        
        // Check if we're switching between horizontal and vertical layouts
        const isOldVertical = oldLayout === 'map-top-info-bottom' || oldLayout === 'map-bottom-info-top';
        const isNewVertical = newLayout === 'map-top-info-bottom' || newLayout === 'map-bottom-info-top';
        
        // Swap dimensions if switching orientation
        if (isOldVertical !== isNewVertical) {
            const temp = this.embedWidth;
            this.embedWidth = this.embedHeight;
            this.embedHeight = temp;
        }
        
        this.layout = newLayout;
    }

    render() {
        return html`
            <mwc-dialog id="modal" class="dialog" hideActions>
                <modal-dialog size="sm" @close="${() => this.open(false)}">
                    <div slot="body" style="overflow: hidden;">
                        <div class="dialog-title">
                            <h2>Static Map Embed Code</h2>
                            <p>Embed a static map and card with just this point using the options below.</p>
                            <hr />
                        </div>

                        <div class="dialog-content">
                            <div class="settings-section">
                                <h3>Customize Embed</h3>

                                <div class="setting-row">
                                    <label>Width (pixels):</label>
                                    <input
                                        type="number"
                                        .value="${this.embedWidth.toString()}"
                                        @input="${this.onWidthInput}"
                                        min="300"
                                        max="2000"
                                        class="size-input"
                                    />
                                </div>

                                <div class="setting-row">
                                    <label>Height (pixels):</label>
                                    <input
                                        type="number"
                                        .value="${this.embedHeight.toString()}"
                                        @input="${this.onHeightInput}"
                                        min="200"
                                        max="2000"
                                        class="size-input"
                                    />
                                </div>

                                <div class="setting-row">
                                    <label>Layout:</label>
                                    <select @change="${this.onLayoutChange}" class="layout-select">
                                        <option value="map-left-info-right" ?selected="${this.layout === 'map-left-info-right'}">Map Left, Info Right</option>
                                        <option value="map-right-info-left" ?selected="${this.layout === 'map-right-info-left'}">Map Right, Info Left</option>
                                        <option value="map-top-info-bottom" ?selected="${this.layout === 'map-top-info-bottom'}">Map Top, Info Bottom</option>
                                        <option value="map-bottom-info-top" ?selected="${this.layout === 'map-bottom-info-top'}">Map Bottom, Info Top</option>
                                        <option value="map-only" ?selected="${this.layout === 'map-only'}">Map Only</option>
                                        <option value="card-only" ?selected="${this.layout === 'card-only'}">Card Only</option>
                                    </select>
                                </div>
                            </div>

                            <div class="code-section">
                                <h3>Direct Link</h3>
                                <div class="code-box">
                                    <code>${this.getEmbedUrl()}</code>
                                </div>
                                <form-button
                                    size="sm"
                                    @click="${this.copyUrl}"
                                    style="margin-top: 8px;"
                                >
                                    <mwc-icon>content_copy</mwc-icon>
                                    Copy URL
                                </form-button>
                            </div>

                            <div class="code-section">
                                <h3>Iframe Embed Code</h3>
                                <div class="code-box">
                                    <code>${this.getIframeCode()}</code>
                                </div>
                                <form-button
                                    size="sm"
                                    @click="${this.copyIframe}"
                                    style="margin-top: 8px;"
                                >
                                    <mwc-icon>content_copy</mwc-icon>
                                    Copy Iframe Code
                                </form-button>
                            </div>
                        </div>
                    </div>
                </modal-dialog>
            </mwc-dialog>
        `;
    }

    static get styles() {
        return css`
            hr {
                border: 0;
                height: 1px;
                background: var(--theme-border-color);
                margin: 16px 0;
            }

            .dialog-title h2 {
                font-size: 1em;
                font-weight: 700;
                text-align: center;
                margin: 0;
                color: var(--main-primary-color);
            }

            .dialog-content {
                display: flex;
                flex-direction: column;
                gap: 24px;
            }

                        /* width */
            mwc-dialog::-webkit-scrollbar, .code-box::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            mwc-dialog::-webkit-scrollbar-track, .code-box::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            mwc-dialog::-webkit-scrollbar-thumb, .code-box::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .settings-section h3,
            .code-section h3 {
                font-size: 1.1em;
                font-weight: 600;
                margin: 0 0 12px 0;
                color: var(--theme-input-color);
            }

            .setting-row {
                display: flex;
                align-items: center;
                gap: 12px;
                margin-bottom: 12px;
            }

            .setting-row label {
                font-weight: 500;
                color: var(--theme-input-color);
            }

            .size-input,
            .layout-select {
                flex: 1;
                padding: 8px 12px;
                border: 1px solid var(--theme-border-color);
                border-radius: 4px;
                font-size: 14px;
                font-family: inherit;
            }

            .size-input:focus,
            .layout-select:focus {
                outline: none;
                border-color: var(--main-primary-color);
            }

            .code-section {
                display: flex;
                flex-direction: column;
            }

            .code-box {
                background: var(--theme-light-scrollbar-color, #f5f5f5);
                border: 1px solid var(--theme-border-color);
                border-radius: 4px;
                padding: 12px;
                overflow-x: auto;
                max-height: 150px;
                overflow-y: auto;
            }

            .code-box code {
                font-family: 'Courier New', monospace;
                font-size: 12px;
                color: var(--theme-input-color);
                word-break: break-all;
                white-space: pre-wrap;
            }

            .dialog-actions {
                display: flex;
                justify-content: flex-end;
                margin-top: 16px;
                padding-top: 16px;
                border-top: 1px solid var(--theme-border-color);
            }

            @media only screen and (max-width: 768px) {
                .setting-row {
                    flex-direction: column;
                    align-items: flex-start;
                }

                .setting-row label {
                    min-width: auto;
                }

                .size-input,
                .layout-select {
                    width: 100%;
                }
            }
        `;
    }
}
